import * as React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { recallAPI, type RecallFilters } from '../services/recallAPI';

/**
 * Query keys for React Query caching
 */
export const recallQueryKeys = {
  all: ['recalls'] as const,
  lists: () => [...recallQueryKeys.all, 'list'] as const,
  list: (
    filters: RecallFilters,
    page: number,
    pageSize: number,
    userId: string | undefined,
    tenantId: string
  ) => [...recallQueryKeys.lists(), { filters, page, pageSize, userId, tenantId }] as const,
  stats: (userId?: string) => [...recallQueryKeys.all, 'stats', { userId }] as const,
  filterOptions: () => [...recallQueryKeys.all, 'filterOptions'] as const
};

/**
 * React Query hook for paginated recall list with caching
 */
export function useRecallListQuery(
  filters: RecallFilters = {},
  page: number = 1,
  pageSize: number = 25,
  userId: string | undefined,
  tenantId: string
) {
  return useQuery({
    queryKey: recallQueryKeys.list(filters, page, pageSize, userId, tenantId),
    queryFn: () => recallAPI.getRecallList(filters, page, pageSize, userId, tenantId),
    enabled: !!tenantId, // Don't run query if tenantId is empty
    staleTime: 5 * 60 * 1000, // 5 minutes (as per CLAUDE.md requirements)
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    // Enable background refetch for better UX
    refetchOnMount: 'always',
    // Keep previous data while loading new data (for pagination)
    placeholderData: previousData => previousData,
    // Don't retry on 403 errors (permission denied) or rate limit errors - these won't succeed on retry
    retry: (failureCount, error: any) => {
      if (
        error?.message?.includes('403') ||
        error?.message?.includes('Forbidden') ||
        error?.message?.includes('permission denied') ||
        error?.message?.includes('Access denied') ||
        error?.name === 'RateLimitError'
      ) {
        return false;
      }
      return failureCount < 3;
    },
    // Add retry delay for rate limiting
    retryDelay: (failureCount, error: any) => {
      if (error?.name === 'RateLimitError' && error?.retryAfter) {
        return error.retryAfter * 1000; // Convert seconds to milliseconds
      }
      // Exponential backoff for other errors
      return Math.min(1000 * 2 ** failureCount, 30000);
    }
  });
}

/**
 * React Query hook for recall statistics
 */
export function useRecallStatsQuery(userId?: string) {
  return useQuery({
    queryKey: recallQueryKeys.stats(userId),
    queryFn: () => recallAPI.getRecallStats(userId),
    staleTime: 10 * 60 * 1000, // 10 minutes for stats
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    // Don't retry on 403 errors (permission denied) - these won't succeed on retry
    retry: (failureCount, error: any) => {
      if (
        error?.message?.includes('403') ||
        error?.message?.includes('Forbidden') ||
        error?.message?.includes('permission denied')
      ) {
        return false;
      }
      return failureCount < 3;
    }
  });
}

/**
 * React Query hook for filter options (campaigns, cities, zip codes)
 * Note: Tenant scoping is handled inside the RPC function
 */
export function useRecallFilterOptionsQuery() {
  return useQuery({
    queryKey: recallQueryKeys.filterOptions(),
    queryFn: () => recallAPI.getFilterOptions(),
    staleTime: 15 * 60 * 1000, // 15 minutes for filter options (static reference data)
    gcTime: 60 * 60 * 1000, // 1 hour
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    // Don't retry on 403 errors (permission denied) or rate limit errors
    retry: (failureCount, error: any) => {
      if (
        error?.message?.includes('403') ||
        error?.message?.includes('Forbidden') ||
        error?.message?.includes('permission denied') ||
        error?.message?.includes('Access denied') ||
        error?.name === 'RateLimitError'
      ) {
        return false;
      }
      return failureCount < 3;
    },
    // Add retry delay for rate limiting
    retryDelay: (failureCount, error: any) => {
      if (error?.name === 'RateLimitError' && error?.retryAfter) {
        return error.retryAfter * 1000;
      }
      return Math.min(1000 * 2 ** failureCount, 30000);
    }
  });
}

/**
 * Mutation hook for exporting recalls to CSV
 */
export function useRecallExportMutation() {
  return useMutation({
    mutationFn: ({
      filters,
      userId,
      tenantId
    }: {
      filters: RecallFilters;
      userId: string | undefined;
      tenantId: string;
    }) => recallAPI.exportRecallsToCSV(filters, userId, tenantId),
    // Optionally invalidate related queries after export
    onSuccess: () => {
      // Could add analytics tracking here
      console.log('Recall export completed successfully');
    }
  });
}

/**
 * Optimized hook that combines all recall list functionality
 * This replaces the old useRecallList hook with React Query caching
 */
export function useOptimizedRecallList(userId: string | undefined, tenantId: string) {
  const queryClient = useQueryClient();

  // State for pagination and filtering
  const [filters, setFilters] = React.useState<RecallFilters>({});
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(25);

  // Main query for recall list
  const recallQuery = useRecallListQuery(filters, currentPage, pageSize, userId, tenantId);

  // Filter options query
  const filterOptionsQuery = useRecallFilterOptionsQuery();

  // Export mutation
  const exportMutation = useRecallExportMutation();

  // Helper functions
  const updateFilters = React.useCallback((newFilters: Partial<RecallFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setCurrentPage(1); // Reset to first page when filters change
  }, []);

  const clearFilters = React.useCallback(() => {
    setFilters({});
    setCurrentPage(1);
  }, []);

  const goToPage = React.useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const changePageSize = React.useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  }, []);

  const exportToCSV = React.useCallback(async () => {
    await exportMutation.mutateAsync({ filters, userId, tenantId });
  }, [exportMutation, filters, userId, tenantId]);

  // Prefetch next page for better UX
  React.useEffect(() => {
    if (recallQuery.data && currentPage < recallQuery.data.totalPages) {
      const nextPageKey = recallQueryKeys.list(
        filters,
        currentPage + 1,
        pageSize,
        userId,
        tenantId
      );
      queryClient.prefetchQuery({
        queryKey: nextPageKey,
        queryFn: () =>
          recallAPI.getRecallList(filters, currentPage + 1, pageSize, userId, tenantId),
        staleTime: 5 * 60 * 1000
      });
    }
  }, [queryClient, recallQuery.data, filters, currentPage, pageSize, userId, tenantId]);

  // Computed pagination info
  const paginationInfo = React.useMemo(() => {
    if (!recallQuery.data) return null;

    const data = recallQuery.data;
    const startIndex = (data.page - 1) * data.pageSize + 1;
    const endIndex = Math.min(data.page * data.pageSize, data.total);

    return {
      startIndex,
      endIndex,
      total: data.total,
      page: data.page,
      totalPages: data.totalPages,
      pageSize: data.pageSize
    };
  }, [recallQuery.data]);

  return {
    // Data
    recalls: recallQuery.data?.recalls || [],
    paginationInfo,
    filterOptions: filterOptionsQuery.data,

    // Loading states
    loading: recallQuery.isLoading,
    filterOptionsLoading: filterOptionsQuery.isLoading,
    exporting: exportMutation.isPending,

    // Error states
    error: recallQuery.error?.message || null,
    filterOptionsError: filterOptionsQuery.error?.message || null,
    exportError: exportMutation.error?.message || null,

    // State
    filters,
    currentPage,
    pageSize,

    // Actions
    updateFilters,
    clearFilters,
    goToPage,
    changePageSize,
    exportToCSV,

    // Query control
    refetch: recallQuery.refetch,
    isRefetching: recallQuery.isRefetching,

    // Cache invalidation helpers
    invalidateRecallList: () => {
      queryClient.invalidateQueries({ queryKey: recallQueryKeys.lists() });
    },
    invalidateFilterOptions: () => {
      queryClient.invalidateQueries({ queryKey: recallQueryKeys.filterOptions() });
    }
  };
}
